import { useState, useRef, useEffect } from "react";
import { ColorPickerPopover } from "./ColorPickerPopover";
import { TableCell } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import { CalendarIcon, Settings } from "lucide-react";
import { cn } from "@/lib/utils";
import { INITIAL_EXPENSE_TYPES } from "@/hooks/useExpenses";
import type { Expense } from "@/hooks/useExpenses";
import { ExpenseTypeCombobox } from "./ExpenseTypeCombobox";
import { TaxCategoryCombobox } from "./TaxCategoryCombobox";
import { Vehicle } from "@/hooks/useVehicles";

interface ExpenseTableCellProps {
  expense: Expense;
  field: string;
  type: "text" | "number" | "currency" | "date" | "select";
  options?: { value: string; label: string }[];
  isEditing: boolean;
  onStartEdit: () => void;
  onUpdate: (value: string | number | null) => void;
  onCancel: () => void;
  onManage?: () => void;
  vehicles: Vehicle[];
  getColor?: (type: "vehicle" | "expense_type" | "paid_by", value: string) => { bg: string; text: string; dot: string };
  onColorChange?: (type: "vehicle" | "expense_type" | "paid_by", value: string, color: string) => void;
  onTabNext?: () => void;
  onTabPrev?: () => void;
}

export const ExpenseTableCell = ({
  expense,
  field,
  type,
  options,
  isEditing,
  onStartEdit,
  onUpdate,
  onCancel,
  onManage,
  vehicles,
  getColor,
  onColorChange,
  onTabNext,
  onTabPrev,
}: ExpenseTableCellProps) => {
  const value = expense[field as keyof Expense];
  const inputRef = useRef<HTMLInputElement>(null);
  const [localValue, setLocalValue] = useState(value?.toString() || "");
  const [dateOpen, setDateOpen] = useState(false);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  useEffect(() => {
    setLocalValue(value?.toString() || "");
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Tab") {
      e.preventDefault();
      handleSubmit();
      if (e.shiftKey) {
        onTabPrev?.();
      } else {
        onTabNext?.();
      }
    } else if (e.key === "Enter") {
      handleSubmit();
    } else if (e.key === "Escape") {
      onCancel();
    }
  };

  const handleSubmit = () => {
    if (type === "number" || type === "currency") {
      const numValue = parseFloat(localValue);
      onUpdate(isNaN(numValue) ? null : numValue);
    } else {
      onUpdate(localValue || null);
    }
  };

  const formatDisplayValue = () => {
    if (value === null || value === undefined || value === "") {
      return <span className="text-muted-foreground">—</span>;
    }

    if (type === "currency") {
      return `$${Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }

    if (type === "date" && typeof value === "string") {
      try {
        return format(parseISO(value), "MMM d, yyyy");
      } catch {
        return value;
      }
    }

    if (field === "vehicle_id") {
      const vehicleColor = getColor ? getColor("vehicle", (value as string) || "operations") : null;
      if (!value || value === "operations") {
        return vehicleColor ? (
          <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium" style={{ backgroundColor: vehicleColor.bg, color: vehicleColor.text }}>Operations</span>
        ) : <span>Operations</span>;
      }
      const vehicle = vehicles.find((v) => v.id === value);
      if (!vehicle) return <span className="text-muted-foreground">—</span>;
      const label = vehicle.name || `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
      return vehicleColor ? (
        <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium" style={{ backgroundColor: vehicleColor.bg, color: vehicleColor.text }}>{label}</span>
      ) : <span>{label}</span>;
    }

    if (field === "paid_by") {
      const paidByColor = getColor ? getColor("paid_by", value as string) : null;
      return paidByColor ? (
        <span className="inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium" style={{ backgroundColor: paidByColor.bg, color: paidByColor.text }}>{String(value)}</span>
      ) : <span>{String(value)}</span>;
    }

    if (type === "number" && field === "mileage") {
      return Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    }

    return String(value);
  };

  if (type === "date") {
    return (
      <TableCell className="p-1">
        <Popover open={dateOpen} onOpenChange={setDateOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                "w-full justify-start text-left font-normal h-9 px-2",
                !value && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {value ? format(parseISO(value as string), "MMM d, yyyy") : "Select date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-popover" align="start">
            <Calendar
              mode="single"
              selected={value ? parseISO(value as string) : undefined}
              defaultMonth={value ? parseISO(value as string) : undefined}
              onSelect={(date) => {
                if (date) {
                  onUpdate(format(date, "yyyy-MM-dd"));
                }
                setDateOpen(false);
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </TableCell>
    );
  }

  if (type === "select") {
    const isExpenseType = field === "expense_type";
    const isTaxCategory = field === "tax_category";

    // Tax category uses a searchable combobox (no grouping)
    if (isTaxCategory && options) {
      return (
        <TableCell className="p-1">
          <TaxCategoryCombobox
            value={(value as string) || ""}
            options={options}
            onChange={(newValue) => onUpdate(newValue)}
            onManage={onManage}
          />
        </TableCell>
      );
    }

    // Expense type uses the searchable combobox
    if (isExpenseType && options) {
      return (
        <TableCell className="p-1">
          <ExpenseTypeCombobox
            value={(value as string) || ""}
            options={options}
            onChange={(newValue) => onUpdate(newValue)}
            onManage={onManage}
            getColor={getColor ? (v) => getColor("expense_type", v) : undefined}
            onColorChange={onColorChange ? (v, c) => onColorChange("expense_type", v, c) : undefined}
          />
        </TableCell>
      );
    }

    // For vehicle_id, null means "Operations"
    const selectValue = field === "vehicle_id" && !value ? "operations" : ((value as string) || "");
    return (
      <TableCell className="p-1">
        <div className="flex items-center gap-0">
          <Select
            value={selectValue}
            onValueChange={(newValue) => {
              if (field === "vehicle_id" && newValue === "operations") {
                onUpdate(null);
              } else {
                onUpdate(newValue);
              }
            }}
          >
             <SelectTrigger className="h-auto min-h-9 border-0 shadow-none focus:ring-0 bg-transparent w-full justify-start text-left whitespace-normal [&>span]:line-clamp-none [&>span]:whitespace-normal [&>span]:text-left pr-6">
              <SelectValue placeholder="Select..." className="whitespace-normal" />
            </SelectTrigger>
            <SelectContent className="bg-popover">
              {options?.map((option) => {
                const isVehicle = field === "vehicle_id";
                const isPaidBy = field === "paid_by";
                const colorType = isVehicle ? "vehicle" : isPaidBy ? "paid_by" : null;
                const optColor = colorType && getColor ? getColor(colorType, option.value) : null;
                return (
                  <SelectItem key={option.value} value={option.value}>
                    <span className="rounded px-1.5 py-0.5 text-xs font-medium" style={optColor ? { backgroundColor: optColor.bg, color: optColor.text } : undefined}>
                      {option.label}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          {onManage && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0 opacity-50 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onManage();
              }}
            >
              <Settings className="h-3 w-3" />
            </Button>
          )}
        </div>
      </TableCell>
    );
  }

  if (isEditing) {
    return (
      <TableCell className="p-1">
        <Input
          ref={inputRef}
          type={type === "currency" || type === "number" ? "number" : "text"}
          step={type === "currency" || field === "mileage" ? "0.01" : undefined}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSubmit}
          className="h-9 w-full border-0 shadow-none focus-visible:ring-1 bg-transparent"
        />
      </TableCell>
    );
  }

  return (
    <TableCell
      className="p-1 cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={onStartEdit}
    >
      <div className="px-2 py-1.5 min-h-[36px] flex items-center">
        {formatDisplayValue()}
      </div>
    </TableCell>
  );
};
